import { CookieConsentService } from '../services/cookie-consent.js';

export interface CookiePreferences {
    necessary: boolean;
    analytics: boolean;
    functional: boolean;
    marketing: boolean;
    third_party: boolean;
}

export interface CookieBannerTranslations {
    title: string;
    message: string;
    acceptAll: string;
    rejectAll: string;
    customize: string;
    settingsTitle: string;
    necessary: string;
    analytics: string;
    functional: string;
    marketing: string;
    thirdParty: string;
    necessaryDesc: string;
    analyticsDesc: string;
    functionalDesc: string;
    marketingDesc: string;
    thirdPartyDesc: string;
    save: string;
    cancel: string;
    privacyPolicy: string;
    cookiePolicy: string;
}

export class CookieBanner {
    private isVisible: boolean = false;
    private modalVisible: boolean = false;
    private consentService: CookieConsentService;
    private currentPreferences: CookiePreferences;
    private onConsentChange?: (preferences: CookiePreferences) => void;
    private translations: CookieBannerTranslations;
    private isProcessing: boolean = false;

    constructor(onConsentChange?: (preferences: CookiePreferences) => void) {
        this.consentService = new CookieConsentService();
        this.onConsentChange = onConsentChange;
        this.currentPreferences = {
            necessary: true,
            analytics: false,
            functional: false,
            marketing: false,
            third_party: false
        };

        // Default Portuguese translations
        this.translations = this.getPortugueseTranslations();

        this.init();
    }

    private async init(): Promise<void> {
        try {
            // Check if user already has consent preferences
            const consent = await this.consentService.getCurrentConsent();

            if (!consent || consent.is_expired) {
                // Show banner for new or expired consent
                this.showBanner();
            } else {
                // User has valid consent, apply preferences
                this.currentPreferences = {
                    necessary: consent.necessary_cookies,
                    analytics: consent.analytics_cookies,
                    functional: consent.functional_cookies,
                    marketing: consent.marketing_cookies,
                    third_party: consent.third_party_cookies
                };

                if (this.onConsentChange) {
                    this.onConsentChange(this.currentPreferences);
                }
            }
        } catch (error) {
            console.warn('Cookie consent initialization failed:', error);
            // Show banner on error to be safe
            this.showBanner();
        }
    }

    private getPortugueseTranslations(): CookieBannerTranslations {
        return {
            title: 'Utilizamos Cookies',
            message: 'Este website utiliza cookies para melhorar a sua experiência. Pode aceitar todos, rejeitar os não essenciais, ou personalizar as suas preferências.',
            acceptAll: 'Aceitar Todos',
            rejectAll: 'Rejeitar Não Essenciais',
            customize: 'Personalizar',
            settingsTitle: 'Definições de Cookies',
            necessary: 'Cookies Essenciais',
            analytics: 'Cookies de Análise',
            functional: 'Cookies Funcionais',
            marketing: 'Cookies de Marketing',
            thirdParty: 'Cookies de Terceiros',
            necessaryDesc: 'Necessários para o funcionamento do website. Não podem ser desativados.',
            analyticsDesc: 'Ajudam-nos a compreender como os visitantes interagem com o website.',
            functionalDesc: 'Permitem funcionalidades melhoradas e personalizadas.',
            marketingDesc: 'Utilizados para publicidade direcionada e análise de marketing.',
            thirdPartyDesc: 'Cookies de serviços externos integrados no website.',
            save: 'Guardar Preferências',
            cancel: 'Cancelar',
            privacyPolicy: 'Política de Privacidade',
            cookiePolicy: 'Política de Cookies'
        };
    }

    private showBanner(): void {
        if (this.isVisible) return;

        this.isVisible = true;
        const banner = this.createBannerElement();
        document.body.appendChild(banner);

        // Add CSS if not already present
        if (!document.getElementById('cookie-banner-styles')) {
            const styles = this.createStyles();
            document.head.appendChild(styles);
        }

        // Animate in
        setTimeout(() => {
            banner.classList.add('cb-visible');
        }, 100);
    }

    private hideBanner(): void {
        const banner = document.getElementById('cookie-banner');
        if (banner) {
            banner.classList.remove('cb-visible');
            setTimeout(() => {
                banner.remove();
            }, 300);
        }
        this.isVisible = false;
    }

    private createBannerElement(): HTMLElement {
        const banner = document.createElement('div');
        banner.id = 'cookie-banner';
        banner.className = 'cookie-banner';
        banner.setAttribute('role', 'dialog');
        banner.setAttribute('aria-labelledby', 'cb-title');
        banner.setAttribute('aria-describedby', 'cb-message');

        banner.innerHTML = `
            <div class="cb-content">
                <div class="cb-text">
                    <h3 id="cb-title" class="cb-title">${this.translations.title}</h3>
                    <p id="cb-message" class="cb-message">${this.translations.message}</p>
                </div>
                <div class="cb-actions">
                    <button type="button" class="cb-btn cb-btn-accept" data-action="accept-all">
                        ${this.translations.acceptAll}
                    </button>
                    <button type="button" class="cb-btn cb-btn-reject" data-action="reject-all">
                        ${this.translations.rejectAll}
                    </button>
                    <button type="button" class="cb-btn cb-btn-customize" data-action="customize">
                        ${this.translations.customize}
                    </button>
                </div>
                <div class="cb-links">
                    <a href="/privacy-policy" class="cb-link">${this.translations.privacyPolicy}</a>
                    <a href="/cookie-policy" class="cb-link">${this.translations.cookiePolicy}</a>
                </div>
            </div>
        `;

        // Add event listeners
        banner.addEventListener('click', this.handleBannerClick.bind(this));

        return banner;
    }

    private createSettingsModal(): HTMLElement {
        const modal = document.createElement('div');
        modal.id = 'cookie-settings-modal';
        modal.className = 'cookie-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-labelledby', 'cs-title');
        modal.setAttribute('aria-modal', 'true');

        modal.innerHTML = `
            <div class="cm-overlay" data-action="close-modal"></div>
            <div class="cm-content">
                <div class="cm-header">
                    <h2 id="cs-title" class="cm-title">${this.translations.settingsTitle}</h2>
                    <button type="button" class="cm-close" data-action="close-modal" aria-label="Fechar">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </button>
                </div>
                <div class="cm-body">
                    <div class="cm-category">
                        <div class="cm-category-header">
                            <h3 class="cm-category-title">${this.translations.necessary}</h3>
                            <label class="cm-toggle cm-toggle-disabled">
                                <input type="checkbox" checked disabled data-category="necessary">
                                <span class="cm-slider"></span>
                            </label>
                        </div>
                        <p class="cm-category-desc">${this.translations.necessaryDesc}</p>
                    </div>
                    
                    <div class="cm-category">
                        <div class="cm-category-header">
                            <h3 class="cm-category-title">${this.translations.analytics}</h3>
                            <label class="cm-toggle">
                                <input type="checkbox" data-category="analytics" ${this.currentPreferences.analytics ? 'checked' : ''}>
                                <span class="cm-slider"></span>
                            </label>
                        </div>
                        <p class="cm-category-desc">${this.translations.analyticsDesc}</p>
                    </div>
                    
                    <div class="cm-category">
                        <div class="cm-category-header">
                            <h3 class="cm-category-title">${this.translations.functional}</h3>
                            <label class="cm-toggle">
                                <input type="checkbox" data-category="functional" ${this.currentPreferences.functional ? 'checked' : ''}>
                                <span class="cm-slider"></span>
                            </label>
                        </div>
                        <p class="cm-category-desc">${this.translations.functionalDesc}</p>
                    </div>
                    
                    <div class="cm-category">
                        <div class="cm-category-header">
                            <h3 class="cm-category-title">${this.translations.marketing}</h3>
                            <label class="cm-toggle">
                                <input type="checkbox" data-category="marketing" ${this.currentPreferences.marketing ? 'checked' : ''}>
                                <span class="cm-slider"></span>
                            </label>
                        </div>
                        <p class="cm-category-desc">${this.translations.marketingDesc}</p>
                    </div>
                    
                    <div class="cm-category">
                        <div class="cm-category-header">
                            <h3 class="cm-category-title">${this.translations.thirdParty}</h3>
                            <label class="cm-toggle">
                                <input type="checkbox" data-category="third_party" ${this.currentPreferences.third_party ? 'checked' : ''}>
                                <span class="cm-slider"></span>
                            </label>
                        </div>
                        <p class="cm-category-desc">${this.translations.thirdPartyDesc}</p>
                    </div>
                </div>
                <div class="cm-footer">
                    <button type="button" class="cb-btn cb-btn-secondary" data-action="close-modal">
                        ${this.translations.cancel}
                    </button>
                    <button type="button" class="cb-btn cb-btn-primary" data-action="save-preferences">
                        ${this.translations.save}
                    </button>
                </div>
            </div>
        `;

        // Add event listeners
        modal.addEventListener('click', this.handleModalClick.bind(this));

        return modal;
    }

    private createStyles(): HTMLElement {
        const styles = document.createElement('style');
        styles.id = 'cookie-banner-styles';
        styles.textContent = `
            /* Cookie Banner Styles */
            .cookie-banner {
                position: fixed;
                bottom: 0;
                left: 0;
                right: 0;
                background: #1a1a1a;
                color: #ffffff;
                padding: 1.5rem;
                box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.1);
                z-index: 10000;
                transform: translateY(100%);
                transition: transform 0.3s ease-in-out;
                border-top: 3px solid #007bff;
            }
            
            .cookie-banner.cb-visible {
                transform: translateY(0);
            }
            
            .cb-content {
                max-width: 1200px;
                margin: 0 auto;
                display: grid;
                grid-template-columns: 1fr auto;
                grid-template-areas: 
                    "text actions"
                    "links links";
                gap: 1rem;
                align-items: center;
            }
            
            .cb-text {
                grid-area: text;
            }
            
            .cb-title {
                margin: 0 0 0.5rem 0;
                font-size: 1.125rem;
                font-weight: 600;
                color: #ffffff;
            }
            
            .cb-message {
                margin: 0;
                font-size: 0.875rem;
                line-height: 1.5;
                color: #e5e5e5;
            }
            
            .cb-actions {
                grid-area: actions;
                display: flex;
                gap: 0.75rem;
                flex-wrap: wrap;
            }
            
            .cb-links {
                grid-area: links;
                display: flex;
                gap: 1rem;
                margin-top: 0.5rem;
            }
            
            .cb-btn {
                padding: 0.625rem 1.25rem;
                border: none;
                border-radius: 6px;
                font-size: 0.875rem;
                font-weight: 500;
                cursor: pointer;
                transition: all 0.2s ease;
                white-space: nowrap;
                text-decoration: none;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                min-height: 40px;
            }
            
            .cb-btn-accept {
                background: #007bff;
                color: #ffffff;
            }
            
            .cb-btn-accept:hover {
                background: #0056b3;
            }
            
            .cb-btn-reject {
                background: #6c757d;
                color: #ffffff;
            }
            
            .cb-btn-reject:hover {
                background: #545b62;
            }
            
            .cb-btn-customize {
                background: transparent;
                color: #ffffff;
                border: 1px solid #6c757d;
            }
            
            .cb-btn-customize:hover {
                background: #6c757d;
                border-color: #6c757d;
            }
            
            .cb-btn-primary {
                background: #007bff;
                color: #ffffff;
            }
            
            .cb-btn-primary:hover {
                background: #0056b3;
            }
            
            .cb-btn-secondary {
                background: transparent;
                color: #6c757d;
                border: 1px solid #6c757d;
            }
            
            .cb-btn-secondary:hover {
                background: #f8f9fa;
                color: #495057;
            }
            
            .cb-link {
                color: #007bff;
                text-decoration: underline;
                font-size: 0.75rem;
            }
            
            .cb-link:hover {
                color: #0056b3;
            }
            
            /* Cookie Settings Modal Styles */
            .cookie-modal {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 10001;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 1rem;
            }
            
            .cm-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: rgba(0, 0, 0, 0.5);
                backdrop-filter: blur(4px);
            }
            
            .cm-content {
                position: relative;
                background: #ffffff;
                border-radius: 12px;
                max-width: 600px;
                width: 100%;
                max-height: 90vh;
                overflow: hidden;
                box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
            }
            
            .cm-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 1.5rem;
                border-bottom: 1px solid #e5e5e5;
            }
            
            .cm-title {
                margin: 0;
                font-size: 1.25rem;
                font-weight: 600;
                color: #1a1a1a;
            }
            
            .cm-close {
                padding: 0.5rem;
                border: none;
                background: none;
                cursor: pointer;
                border-radius: 4px;
                color: #6c757d;
            }
            
            .cm-close:hover {
                background: #f8f9fa;
                color: #495057;
            }
            
            .cm-body {
                padding: 1.5rem;
                max-height: 60vh;
                overflow-y: auto;
            }
            
            .cm-category {
                margin-bottom: 2rem;
            }
            
            .cm-category:last-child {
                margin-bottom: 0;
            }
            
            .cm-category-header {
                display: flex;
                align-items: center;
                justify-content: space-between;
                margin-bottom: 0.5rem;
            }
            
            .cm-category-title {
                margin: 0;
                font-size: 1rem;
                font-weight: 500;
                color: #1a1a1a;
            }
            
            .cm-category-desc {
                margin: 0;
                font-size: 0.875rem;
                color: #6c757d;
                line-height: 1.4;
            }
            
            /* Toggle Switch Styles */
            .cm-toggle {
                position: relative;
                display: inline-block;
                width: 48px;
                height: 24px;
            }
            
            .cm-toggle input {
                opacity: 0;
                width: 0;
                height: 0;
            }
            
            .cm-slider {
                position: absolute;
                cursor: pointer;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: #ccc;
                transition: 0.2s;
                border-radius: 24px;
            }
            
            .cm-slider:before {
                position: absolute;
                content: "";
                height: 18px;
                width: 18px;
                left: 3px;
                bottom: 3px;
                background-color: white;
                transition: 0.2s;
                border-radius: 50%;
            }
            
            .cm-toggle input:checked + .cm-slider {
                background-color: #007bff;
            }
            
            .cm-toggle input:checked + .cm-slider:before {
                transform: translateX(24px);
            }
            
            .cm-toggle-disabled .cm-slider {
                background-color: #007bff;
                opacity: 0.6;
                cursor: not-allowed;
            }
            
            .cm-toggle-disabled input + .cm-slider:before {
                transform: translateX(24px);
            }
            
            .cm-footer {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                gap: 0.75rem;
                padding: 1.5rem;
                border-top: 1px solid #e5e5e5;
                background: #f8f9fa;
            }
            
            /* Responsive Design */
            @media (max-width: 768px) {
                .cookie-banner {
                    padding: 1rem;
                }
                
                .cb-content {
                    grid-template-columns: 1fr;
                    grid-template-areas: 
                        "text"
                        "actions"
                        "links";
                    text-align: center;
                }
                
                .cb-actions {
                    justify-content: center;
                    flex-direction: column;
                    width: 100%;
                }
                
                .cb-btn {
                    width: 100%;
                    justify-content: center;
                }
                
                .cb-links {
                    justify-content: center;
                    flex-wrap: wrap;
                    margin-top: 1rem;
                }
                
                .cm-content {
                    margin: 0.5rem;
                    width: calc(100% - 1rem);
                }
                
                .cm-footer {
                    flex-direction: column;
                }
                
                .cm-footer .cb-btn {
                    width: 100%;
                }
            }
            
            /* Accessibility */
            .cb-btn:focus,
            .cm-close:focus,
            .cm-toggle input:focus + .cm-slider {
                outline: 2px solid #007bff;
                outline-offset: 2px;
            }
            
            @media (prefers-reduced-motion: reduce) {
                .cookie-banner,
                .cb-btn,
                .cm-slider,
                .cm-slider:before {
                    transition: none;
                }
            }
        `;

        return styles;
    }

    private async handleBannerClick(event: Event): Promise<void> {
        if (this.isProcessing) return;

        const target = event.target as HTMLElement;
        const action = target.getAttribute('data-action');

        if (!action) return;

        this.isProcessing = true;

        try {
            switch (action) {
                case 'accept-all':
                    await this.acceptAll();
                    break;
                case 'reject-all':
                    await this.rejectAll();
                    break;
                case 'customize':
                    this.showSettingsModal();
                    break;
            }
        } catch (error) {
            console.error('Cookie banner action failed:', error);
        } finally {
            this.isProcessing = false;
        }
    }

    private async handleModalClick(event: Event): Promise<void> {
        const target = event.target as HTMLElement;
        const action = target.getAttribute('data-action');

        if (!action) return;

        try {
            switch (action) {
                case 'close-modal':
                    this.hideSettingsModal();
                    break;
                case 'save-preferences':
                    await this.saveCustomPreferences();
                    break;
            }
        } catch (error) {
            console.error('Cookie modal action failed:', error);
        }
    }

    private async acceptAll(): Promise<void> {
        const preferences: CookiePreferences = {
            necessary: true,
            analytics: true,
            functional: true,
            marketing: true,
            third_party: true
        };

        await this.savePreferences(preferences);
    }

    private async rejectAll(): Promise<void> {
        const preferences: CookiePreferences = {
            necessary: true,
            analytics: false,
            functional: false,
            marketing: false,
            third_party: false
        };

        await this.savePreferences(preferences);
    }

    private async saveCustomPreferences(): Promise<void> {
        const modal = document.getElementById('cookie-settings-modal');
        if (!modal) return;

        const checkboxes = modal.querySelectorAll('input[type="checkbox"]') as NodeListOf<HTMLInputElement>;
        const preferences: CookiePreferences = {
            necessary: true,
            analytics: false,
            functional: false,
            marketing: false,
            third_party: false
        };

        checkboxes.forEach(checkbox => {
            const category = checkbox.getAttribute('data-category') as keyof CookiePreferences;
            if (category && category in preferences) {
                preferences[category] = checkbox.checked;
            }
        });

        await this.savePreferences(preferences);
        this.hideSettingsModal();
    }

    private async savePreferences(preferences: CookiePreferences): Promise<void> {
        try {
            await this.consentService.createConsent({
                analytics_cookies: preferences.analytics,
                functional_cookies: preferences.functional,
                marketing_cookies: preferences.marketing,
                third_party_cookies: preferences.third_party,
                consent_language: 'pt-PT'
            });

            this.currentPreferences = preferences;

            if (this.onConsentChange) {
                this.onConsentChange(preferences);
            }

            this.hideBanner();

            // Show success message
            this.showConsentConfirmation();

        } catch (error) {
            console.error('Failed to save cookie preferences:', error);
            alert('Erro ao guardar preferências de cookies. Tente novamente.');
        }
    }

    private showSettingsModal(): void {
        if (this.modalVisible) return;

        this.modalVisible = true;
        const modal = this.createSettingsModal();
        document.body.appendChild(modal);

        // Focus management
        const firstFocusable = modal.querySelector('button, input') as HTMLElement;
        if (firstFocusable) {
            firstFocusable.focus();
        }

        // Escape key handler
        const escapeHandler = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                this.hideSettingsModal();
                document.removeEventListener('keydown', escapeHandler);
            }
        };
        document.addEventListener('keydown', escapeHandler);
    }

    private hideSettingsModal(): void {
        const modal = document.getElementById('cookie-settings-modal');
        if (modal) {
            modal.remove();
        }
        this.modalVisible = false;
    }

    private showConsentConfirmation(): void {
        const notification = document.createElement('div');
        notification.style.cssText = `
            position: fixed;
            bottom: 20px;
            left: 50%;
            transform: translateX(-50%);
            background: #28a745;
            color: white;
            padding: 1rem 1.5rem;
            border-radius: 6px;
            box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
            z-index: 10002;
            font-size: 0.875rem;
            font-weight: 500;
            animation: slideUp 0.3s ease-out;
        `;
        notification.textContent = 'Preferências de cookies guardadas com sucesso!';
        document.body.appendChild(notification);

        // Add animation style if not present
        if (!document.getElementById('cookie-notification-animation')) {
            const animStyle = document.createElement('style');
            animStyle.id = 'cookie-notification-animation';
            animStyle.textContent = `
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateX(-50%) translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(-50%) translateY(0);
                    }
                }
            `;
            document.head.appendChild(animStyle);
        }

        setTimeout(() => {
            notification.remove();
        }, 3000);
    }

    // Public methods for external control
    public async getCurrentPreferences(): Promise<CookiePreferences> {
        return this.currentPreferences;
    }

    public showSettings(): void {
        this.showSettingsModal();
    }

    public async updatePreferences(preferences: Partial<CookiePreferences>): Promise<void> {
        const updatedPreferences = { ...this.currentPreferences, ...preferences };
        await this.savePreferences(updatedPreferences);
    }

    // Method to re-show banner (for testing or manual trigger)
    public resetAndShow(): void {
        this.isVisible = false;
        this.showBanner();
    }
}


